import React from 'react';
import DateMenu from './DateMenu';
import FieldMenu from './FieldMenu';
import CycleButtonBox from './CycleButtonBox';
import PropTypes from 'prop-types';
import MenuLink from './MenuLink';
import DomainButton from './DomainButton';

/**
 * MenuBox renders most of the menu controls for the app.
 *
 *
 * @reactProps {Object} data - the app data object, includes fields, cycle, and unit metadata
 * @reactProps {string[]} dates - an array of dates/dirs where images are located
 * @reactProps {string} selectedDate - the current date selection for display
 * @reactProps {function} handleDateChange - passes new selections up to main
 * @reactProps {function} handleCycleChange - passes new selections up to main
 * @reactProps {function} handleFieldChange - passes new selections up to main
 */
export default class MenuBox extends React.Component {
  state = {};

  /**
   * Create the control structure that inhibits multiple
   * menus being open at once.
   */
  componentDidMount() {
    this.createSwitchboard();
  }

  /**
   * The switchboard data structure is a map set to state.
   * Only menu in the map can be open simultaneously.
   * The fields from this.props.data are mapped to 0s.
   * (thus menus built from other data are not controlled
   * by switchboard, i.e. date menu or dir menu)
   */
  createSwitchboard = () => {
    const menuSwitchboard = new Map();
    Object.keys(this.props.data['fields']).forEach(fieldname => {
      menuSwitchboard.set(fieldname, false);
    });

    this.setState({ menuSwitchboard });
  };

  /**
   * Dates menu has its own toggle. It is either open or not,
   * which is saved to MenuBox state, regardless of the field menus.
   */
  handleDateMenuToggle = () => {
    this.setState({ open: !this.state.open });
  };

  /**
   * Toggling field menus open or closed.
   *
   * @param {string} field - the name of the field, value of the
   * field menu passed up when it is clicked, corresponds to the
   * names mapped in switchboard
   */
  handleSwitchboardToggle = field => {
    let changed = this.state.menuSwitchboard;
    // if a closed menu has been clicked before
    if (this.state.closeMeNext) {
      // and this field menu is not the same as the previous one
      if (this.state.closeMeNext !== field) {
        // flip the switch on the previous one (close it)
        changed.set(this.state.closeMeNext, false);
      }
    }
    // now flip the switch of the field of the one we want
    // so if closeMeNext === field, we will close it
    const on = this.state.menuSwitchboard.get(field);
    changed.set(field, !on);
    // update the state with the change and note this field should be closed next
    this.setState({ menuSwitchboard: changed, closeMeNext: field });
  };

  /**
   * Renders menus, controls, and menu-like displays of information about
   * the current image product selection.
   * @return div > DomainButton, DateMenu, CycleButtonBox, FieldMenus, and MenuLinks
   */
  render() {
    const { open } = this.state;
    const data = this.props.data;
    const cycles = Object.keys(this.props.data['cycles']);

    if (!this.state.menuSwitchboard) {
      return <div />;
    } else {
      return (
        <div className="menu-box">
          <div className="selected-date">
            <h4 className="date-label">Current date:</h4>
            <p>{this.props.selectedDate}</p>
          </div>
          <DomainButton onClick={this.props.handleDomainOftheDay} />
          <DateMenu
            title="Forecast Dates"
            dates={this.props.dates}
            handleChange={this.props.handleDateChange}
            handleMenuToggle={this.handleDateMenuToggle}
            open={open}
          />
          <CycleButtonBox
            classname="cycles"
            onEvent={this.props.handleCycleChange}
            cycles={cycles}
            handleMenuToggle={this.handleMenuToggle}
            open={open}
          />
          {Object.keys(this.props.data['fields']).map((m, i) => (
            <FieldMenu
              key={i}
              title={m}
              menuData={
                Object.keys(data['fields'][m]) ? data['fields'][m] : null
              }
              handleFieldChange={this.props.handleFieldChange}
              handleMenuToggle={this.handleSwitchboardToggle}
              open={this.state.menuSwitchboard.get(m)}
            />
          ))}
        </div>
      );
    }
  }
}

MenuBox.propTypes = {
  // data is from a json file structured specifically to populate
  // wof-essential menus. cycles, units, and fields are all included in the json
  // the menus will not display without this information
  data: PropTypes.object.isrequired,
  // dates is an array that populates the date menu. it's the list of directories
  // where the images are located
  dates: PropTypes.arrayOf(PropTypes.string),
  // the date we need to display because it is the directory we pointing to right now
  selectedDate: PropTypes.string,
  // the handle funcs take date, cycle, and fields as args and Main state updates
  // as does the path name for the image product
  handleDateChange: PropTypes.func.isRequired,
  handleCycleChange: PropTypes.func.isRequired,
  handleFieldChange: PropTypes.func.isRequired
};



// WEBPACK FOOTER //
// ./src/MenuBox.js